import React, { useState, useEffect, useCallback } from 'react';
import Reveal from './Reveal';
import './Gallery.css';

const sculptures = [
  {
    id: 1,
    title: 'My Love',
    description: 'Duas figuras entrelaçadas num abraço — a obra que abre a coleção, em terracota de tons quentes.',
    image: '/arte1.jpeg',
    technique: 'Barro / terracota',
    year: '2023'
  },
  {
    id: 2,
    title: 'Baianinha Morena',
    description: 'Arte tridimensional que celebra as raízes afro-brasileiras e a cultura popular.',
    image: '/baianinha.jpeg',
    technique: 'Barro / terracota',
    year: '2023'
  },
  {
    id: 3,
    title: 'Pituca, Alma da Tribo',
    description: 'Trabalho que reflete a evolução criadora original do artista ao longo de décadas.',
    image: '/almadatribo.jpg',
    technique: 'Barro / terracota',
    year: '2023'
  },
  {
    id: 4,
    title: 'Duelo de Barro',
    description: 'Movimento e tensão captados no barro — a paixão pela escultura descoberta em Barra Mansa.',
    image: '/chinesejaponeslutando.jpeg',
    technique: 'Barro / terracota',
    year: '2023'
  },
  {
    id: 5,
    title: 'Índio Guarani, Tupi',
    description: 'Figura em tamanho expressivo que impressionou a comunidade local.',
    image: '/homem.jpeg',
    technique: 'Barro / terracota',
    year: '2023'
  },
  {
    id: 6,
    title: 'Sinhazinha Africana',
    description: 'Obra preparada para a primeira exposição oficial do artista.',
    image: '/idosa.jpeg',
    technique: 'Barro / terracota',
    year: '2023'
  },
  {
    id: 7,
    title: 'Sabiá Laranjeira',
    description: 'Técnica autodidata refinada ao longo de mais de 30 anos de ofício.',
    image: '/passaroArte.jpg',
    technique: 'Barro / terracota',
    year: '2023'
  }
];

const Gallery = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const isOpen = activeIndex !== null;

  const close = useCallback(() => setActiveIndex(null), []);
  const next = useCallback(
    () => setActiveIndex((i) => (i + 1) % sculptures.length),
    []
  );
  const prev = useCallback(
    () => setActiveIndex((i) => (i - 1 + sculptures.length) % sculptures.length),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, close, next, prev]);

  const active = isOpen ? sculptures[activeIndex] : null;

  return (
    <section id="gallery" className="gallery section">
      <div className="container">
        <Reveal>
          <span className="section-eyebrow">Esculturas em barro</span>
          <h2 className="section-title">A Coleção</h2>
          <p className="section-subtitle">
            Sete obras moldadas à mão em terracota, fruto de uma técnica autodidata
            desenvolvida ao longo de mais de 30 anos. Clique para ampliar.
          </p>
        </Reveal>

        <div className="gallery-grid">
          {sculptures.map((sculpture, index) => (
            <Reveal
              as="button"
              key={sculpture.id}
              className="gallery-item"
              delay={(index % 3) * 80}
              onClick={() => setActiveIndex(index)}
              aria-label={`Ampliar escultura ${sculpture.title}`}
            >
              <div className="image-container">
                <img
                  src={sculpture.image}
                  alt={`Escultura "${sculpture.title}" em barro por Varella Sambista`}
                  className="sculpture-image"
                  loading="lazy"
                />
                <span className="zoom-hint" aria-hidden="true">＋</span>
              </div>
              <div className="item-info">
                <h3 className="item-title">{sculpture.title}</h3>
                <p className="item-meta">{sculpture.technique} · {sculpture.year}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {isOpen && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={active.title}
          onClick={close}
        >
          <button className="lb-close" onClick={close} aria-label="Fechar">×</button>
          <button
            className="lb-nav lb-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Anterior"
          >‹</button>

          <figure className="lb-figure" onClick={(e) => e.stopPropagation()}>
            <img src={active.image} alt={`Escultura "${active.title}" em barro`} className="lb-image" />
            <figcaption className="lb-caption">
              <h3>{active.title}</h3>
              <p className="lb-meta">{active.technique} · {active.year}</p>
              <p className="lb-desc">{active.description}</p>
            </figcaption>
          </figure>

          <button
            className="lb-nav lb-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Próxima"
          >›</button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
